import { EFontWeight } from 'src/styles/theme/components/NGText/NGText.enum';
import { EMediaQuery } from 'src/styles/theme/utils/enums';
import { createGlobalStyle, css, DefaultTheme } from 'styled-components';
import { addTransition, mq } from './utils/functions';

const GlobalStyles = createGlobalStyle<{ theme: DefaultTheme }>`
//========================================================================================================
// GENERAL
//========================================================================================================
* {
	box-sizing: border-box;

  // disable globally tap highlight on mobile devices
  -webkit-tap-highlight-color: transparent;
}
*::before {
	box-sizing: border-box;
}
*::after {
	box-sizing: border-box;
}

#auth-iframe {
  height: 500px;
	max-height: 100%;
}

:root {
  --onboard-font-family-normal: "Lato";
  --onboard-modal-z-index: 999;
  --onboard-modal-border-radius: 10px;
  --onboard-modal-border-color: red;
}

body {
  ${(p) => css`
		margin: 0 auto;
		max-width: ${EMediaQuery.xl};

		padding: 0 100px;

		min-height: 100vh;
		background: ${p.theme.colors.black};

		${mq(
			EMediaQuery.xl,
			css`
				padding: 0 100px;
			`
		)}
		${mq(
			EMediaQuery.lg,
			css`
				padding: 0 64px;
			`
		)}
		${mq(
			EMediaQuery.md,
			css`
				padding: 0px;
			`
		)}

		${mq(
			EMediaQuery.sm,
			css`
				padding: 0px;
			`
		)}
	`}
}

html {
  height: 100%;
  overflow: hidden;
}

h1,
h2,
h3,
h4,
h5,
h6,
button,
span,
a,
p,
input,
textarea {
	color: ${(p) => p.theme.colors.text.white};

	margin: 0;
}

h1,
h2,
h3,
h4,
h5,
h6,
button {
  font-family: 'Cinzel', serif;
  font-weight: ${EFontWeight.bold};
}

b {
  font-weight: ${EFontWeight.bold};
}


p,
span,
input,
textarea {
  font-family: 'Lato', sans-serif;
  font-weight: 400;
}

ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

a {
  text-decoration: none;
}

section {
  position: relative;
}

.circular {
  border-radius: 50%;
  overflow: hidden;
}

.bottom-location {
  position: fixed;
  height: 0px;
  bottom: 0;
  left: 0;
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
textarea:-webkit-autofill,
textarea:-webkit-autofill:hover,
textarea:-webkit-autofill:focus,
select:-webkit-autofill,
select:-webkit-autofill:hover,
select:-webkit-autofill:focus {
  -webkit-text-fill-color: ${({ theme }) => theme.colors.text.white};
  box-shadow: 0 0 0px 1000px #00000000 inset;
  -webkit-transition: background-color 5000s ease-in-out 0s;
  transition: background-color 5000s ease-in-out 0s;
}

input,
textarea {
  background-color: transparent;
}

//========================================================================================================
// SCROLLBAR
//========================================================================================================

::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  box-shadow: inset 0 0 6px ${(p) => p.theme.colors.black}50;
  border-radius: 6px;
}

::-webkit-scrollbar-thumb {
		border-radius: 6px;
		box-shadow: inset 0 0 6px ${(p) => p.theme.colors.gold};
	}

// firefox
:root {
  scrollbar-face-color: rgba(142, 142, 142, 0.4); /* Firefox 63 compatibility */
  scrollbar-track-color: rgba(15, 15, 15, 0.4); /* Firefox 63 compatibility */
  scrollbar-color: rgba(142, 142, 142, 0.4) rgba(15, 15, 15, 0.4);
  scrollbar-width: thin;
}

//========================================================================================================
// ANIMATIONS
//========================================================================================================

@keyframes perspectiveAnimation {
    0% {
        perspective: 49px;
    }
    50% {
        perspective: 100px;
    }
    100% {
        perspective: 49px;
    }
}

@keyframes fadeIn {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
}

@keyframes fadeOut {
		from {
			opacity: 1;
		}
		to {
			opacity: 0;
		}
}

// create keyframes rotate 360deg
@keyframes rotate360 {
  0% {
    transform: rotate(0deg) scale(1);
  }
  50% {
    transform: rotate(180deg) scale(1.02);
  }

  100% {
    transform: rotate(360deg) scale(1);
  }
}

// spin animation
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}


//========================================================================================================
// FADE (react-transition-group)
.fade-enter {
  opacity: 0;
}

.fade-exit {
  opacity: 1;
}

.fade-enter-active {
  opacity: 1;
}

.fade-exit-active {
  opacity: 0;
}

.fade-enter-active,
.fade-exit-active {
  ${addTransition(undefined, 0.5)}
}

// switch to customization view
.fade-customization-enter {
  opacity: 0;
  transform: scale(1.05);
}

.fade-customization-exit {
  opacity: 1;
  transform: scale(1.0);
}

.fade-customization-enter-active {
  opacity: 1;
  transform: scale(1.0);
}

.fade-customization-exit-active {
  opacity: 0;
  transform: scale(1.05);
}

// reverse
.fade-customization-reverse-enter {
  opacity: 0;
  transform: scale(0.95);
}

.fade-customization-reverse-exit {
  opacity: 1;
  transform: scale(1.0);
}

.fade-customization-reverse-enter-active {
  opacity: 1;
  transform: scale(1.0);
}

.fade-customization-reverse-exit-active {
  opacity: 0;
  transform: scale(0.95);
}

.fade-customization-enter-active,
.fade-customization-exit-active,
.fade-customization-reverse-enter-active,
.fade-customization-reverse-exit-active {
  ${addTransition(undefined, 0.5)}
}

.fade-quiz-enter {
  opacity: 0;
}

.fade-quiz-exit {
  opacity: 1;
  transform: scale(1.0);
}

.fade-quiz-enter-active {
  opacity: 1;
}

.fade-quiz-exit-active {
  opacity: 0;
  transform: scale(0.98);
}

.fade-quiz-enter-active,
.fade-quiz-exit-active {
  ${addTransition(undefined, 0.4)}
}

.switch-page-enter {
  opacity: 0;
  //transform: scale(1.05)
}

.switch-page-exit {
  opacity: 1;
  //transform: scale(1)
}

.switch-page-enter-active {
  opacity: 1;
  //transform: scale(1)
}
.switch-page-exit-active {
  opacity: 0;
  //transform: scale(0.95)
}

.switch-page-enter-active,
.switch-page-exit-active {
  ${addTransition(undefined, 0.5)}
}

// SUBAPGE TRANSITIONS
.page-transition-enter {
    opacity: 0;
}

.page-transition-enter-active {
  opacity: 1;
}

.page-transition-exit {
  opacity: 1;
}

.page-transition-exit-active {
  opacity: 0;
}

body:not(.is-not-moving) {
  .page-transition-enter {
    transform: translateX(60%) scale(0.9);
  }

  .page-transition-exit {
    transform: translateX(0) scale(1.0);
  }

  .page-transition-enter-active {
    transform: translateX(0) scale(1.0);
  }

  .page-transition-exit-active {
    transform: translateX(-60%) scale(0.9);
  }

  &.is-moving-to-left {
    .page-transition-enter {
      transform: translateX(-60%) scale(0.9);
    }

    .page-transition-exit {
      transform: translateX(0) scale(1.0);
    }

    .page-transition-enter-active {
      transform: translateX(0) scale(1.0);
    }

    .page-transition-exit-active {
      transform: translateX(60%) scale(0.9);
    }
  }
}

.page-transition-enter-active,
.page-transition-exit-active {
  ${addTransition(undefined, 0.4)}
}

// TOASTS
.fade-toast-enter {
  opacity: 0;
  max-height: 0;
}

.fade-toast-exit {
  opacity: 1;
  max-height: 400px;
}

.fade-toast-enter-active {
  opacity: 1;
  max-height: 400px;
}

.fade-toast-exit-active {
  opacity: 0;
  max-height: 0;
}

.fade-toast-enter-active,
.fade-toast-exit-active {
  ${addTransition(undefined, 0.4)}
}

// TOASTS
.blink-enter {
  opacity: 0.2;
}

.blink-exit {
  opacity: 1;
}

.blink-enter-active {
  opacity: 1;
}

.blink-exit-active {
  opacity: 0.2;
}

.blink-enter-active,
.blink-exit-active {
  ${addTransition(undefined, 0.4)}
}
//========================================================================================================


//========================================================================================================
// RICHTEXT
//========================================================================================================
p.richtext {
  line-height: 1.5rem;
  hyphens: auto;
}


  .rt-inlinecode {
  & p,
  & span,
  & input,
  & textarea {
    font-family: 'Courier Prime', monospace;
    font-weight: 400;
  }

  background-color: ${(p) => p.theme.colors.success + '20'};

	border-radius: 4px;

	padding: 4px 4px;

  color: ${(p) => p.theme.colors.text.solidity};
}

.rt-inlinecode {
  overflow-wrap: anywhere;
  hyphens: none;
}

table .rt-inlinecode {
  overflow-wrap: break-word;

  hyphens: none;
}

.richtext,
.quote {
  &.rt-link {
    color: ${(p) => p.theme.colors.text.darkGold};
    text-decoration: underline;

    font-size: inherit;

    cursor: pointer;

    overflow-wrap: break-word;
    hyphens: none;

    ${addTransition()}

    @media (hover: hover) {
      &:hover {
        opacity: 0.8;
      }
    }
  }

  &.cdx-marker,
  .cdx-marker {
    color: ${(p) => p.theme.colors.text.white};
    background-color: ${(p) => p.theme.colors.error};
  }
}

.richtext i .rt-link,
.richtext .rt-link i {
  overflow-wrap: anywhere;
}

.richtext.word-wrap-anywhere {
  overflow-wrap: anywhere;
  hyphens: none;
}

//========================================================================================================
// SYNTAX HIGHILIGHTING override
//========================================================================================================

.language-cairo {
  .token {
    &.attribute {
      color: #0ACB1D !important;
    }
  }
}

//========================================================================================================
// FONTS
//========================================================================================================

@font-face {
  font-family: 'icomoon';
  src:  url('/fonts/icomoon.eot?ax6gdu');
  src:  url('/fonts/icomoon.eot?ax6gdu#iefix') format('embedded-opentype'),
    url('/fonts/icomoon.ttf?ax6gdu') format('truetype'),
    url('/fonts/icomoon.woff?ax6gdu') format('woff'),
    url('/fonts/icomoon.svg?ax6gdu#icomoon') format('svg');
  font-weight: normal;
  font-style: normal;
  font-display: block;
}

[class^="icon-"], [class*=" icon-"] {
  /* use !important to prevent issues with browser extensions that change fonts */
  font-family: 'icomoon' !important;
  speak: never;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;

  /* Better Font Rendering =========== */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.icon-logout:before {
  content: "\\e965";
}
.icon-login:before {
  content: "\\e964";
}
.icon-settings:before {
  content: "\\e91d";
}
.icon-trash:before {
  content: "\\e963";
}
.icon-anvil:before {
  content: "\\e961";
}
.icon-cross:before {
  content: "\\e962";
}
.icon-filter:before {
  content: "\\e960";
}
.icon-bulletpoints:before {
  content: "\\e95e";
}
.icon-support:before {
  content: "\\e95f";
}
.icon-share:before {
  content: "\\e95d";
}
.icon-nodeguardians:before {
  content: "\\e95c";
}
.icon-discord:before {
  content: "\\e900";
}
.icon-link:before {
  content: "\\e901";
}
.icon-telegram:before {
  content: "\\e902";
}
.icon-faq:before {
  content: "\\e95a";
}
.icon-twitter:before {
  content: "\\e95b";
}

.icon-lightBulb:before {
  content: "\\e959";
}

.icon-advanced_design_patterns:before { /* .path1 */
  content: "\\e939";
}
/* .icon-advanced_design_patterns .path2:before {
  content: "\\e93a";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
}
.icon-advanced_design_patterns .path3:before {
  content: "\\e93b";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
} */
.icon-advanced_security:before { /* .path1 */
  content: "\\e93c";
}
/* .icon-advanced_security .path2:before {
  content: "\\e93d";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
}
.icon-advanced_security .path3:before {
  content: "\\e93e";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
} */
.icon-data_algorithms:before { /* .path1 */
  content: "\\e93f";
}
/* .icon-data_algorithms .path2:before {
  content: "\\e940";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
}
.icon-data_algorithms .path3:before {
  content: "\\e941";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
} */
.icon-design_patterns:before { /* .path1 */
  content: "\\e942";
}/*
.icon-design_patterns .path2:before {
  content: "\\e943";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
}
.icon-design_patterns .path3:before {
  content: "\\e944";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
} */
.icon-ecosystems:before { /* .path1 */
  content: "\\e945";
}
/* .icon-ecosystems .path2:before {
  content: "\\e946";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
}
.icon-ecosystems .path3:before {
  content: "\\e947";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
} */
.icon-history:before { /* .path1 */
  content: "\\e948";
}
/* .icon-history .path2:before {
  content: "\\e949";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
}
.icon-history .path3:before {
  content: "\\e94a";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
} */
.icon-low_level_programming:before { /* .path1 */
  content: "\\e94b";
}
/* .icon-low_level_programming .path2:before {
  content: "\\e94c";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
}
.icon-low_level_programming .path3:before {
  content: "\\e94d";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
} */
.icon-number_of_quests:before {
  content: "\\e94e";
}
.icon-security:before { /* .path1 */
  content: "\\e94f";
}
/* .icon-security .path2:before {
  content: "\\e950";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
}
.icon-security .path3:before {
  content: "\\e951";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
} */
.icon-skills:before {
  content: "\\e952";
}
.icon-tech_protocols:before { /* .path1 */
  content: "\\e953";
}
/* .icon-tech_protocols .path2:before {
  content: "\\e954";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
}
.icon-tech_protocols .path3:before {
  content: "\\e955";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
} */
.icon-tools:before { /* .path1 */
  content: "\\e956";
}
/* .icon-tools .path2:before {
  content: "\\e957";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
}
.icon-tools .path3:before {
  content: "\\e958";
  margin-left: -1em;
  color: rgb(255, 255, 255);
  opacity: 0.2;
}

 */

.icon-weapon:before {
  content: "\\e938";
}
.icon-bag:before {
  content: "\\e937";
}
.icon-refresh:before {
  content: "\\e92d";
}
.icon-weapon-shield:before {
  content: "\\e92f";
}
.icon-armor:before {
  content: "\\e930";
}
.icon-boots:before {
  content: "\\e931";
}
.icon-gloves:before {
  content: "\\e932";
}
.icon-helmet:before {
  content: "\\e933";
}
.icon-validation-fail:before {
  content: "\\e934";
}
.icon-validation-success:before {
  content: "\\e935";
}
.icon-hashtag:before {
  content: "\\e936";
}

.icon-avatar:before {
  content: "\\e91b";
}
.icon-campaign:before {
  content: "\\e91c";
}
.icon-character:before {
  content: "\\e91d";
}
.icon-dev_hub:before {
  content: "\\e91e";
}
.icon-economy:before {
  content: "\\e91f";
}
.icon-inventory:before {
  content: "\\e920";
}
.icon-journal:before {
  content: "\\e921";
}
.icon-overview2:before {
  content: "\\e922";
}
.icon-protocols:before {
  content: "\\e923";
}
.icon-protocols2:before {
  content: "\\e924";
}
.icon-quests2:before {
  content: "\\e925";
}
.icon-quests:before {
  content: "\\e916";
}
.icon-quill:before {
  content: "\\e926";
}
.icon-shield-warning:before {
  content: "\\e927";
}
.icon-skill_tree:before {
  content: "\\e928";
}
.icon-staking:before {
  content: "\\e929";
}
.icon-tech_primitives:before {
  content: "\\e92b";
}
.icon-theory:before {
  content: "\\e92c";
}
.icon-card:before {
  content: "\\e912";
}
.icon-list:before {
  content: "\\e913";
}
.icon-overview:before {
  content: "\\e914";
}
.icon-shield_warning:before {
  content: "\\e915";
}
.icon-status:before {
  content: "\\e918";
}
.icon-sword:before {
  content: "\\e92a";
}
.icon-tree:before {
  content: "\\e91a";
}
.icon-diamond:before {
  content: "\\e90f";
}
.icon-github:before {
  content: "\\e911";
}
.icon-email:before {
  content: "\\e903";
}
.icon-content_copy:before {
  content: "\\e910";
}
.icon-keyboard_arrow_down:before {
  content: "\\e904";
}
.icon-keyboard_arrow_left:before {
  content: "\\e905";
}
.icon-keyboard_arrow_right:before {
  content: "\\e906";
}
.icon-keyboard_arrow_up:before {
  content: "\\e907";
}
.icon-keyboard_backspace:before {
  content: "\\e908";
}
.icon-check:before {
  content: "\\e909";
}
.icon-wallet:before {
  content: "\\e92e";
}
.icon-east:before {
  content: "\\e90b";
}
.icon-north:before {
  content: "\\e90c";
}
.icon-south:before {
  content: "\\e90d";
}
.icon-west:before {
  content: "\\e90e";
}


`;

export default GlobalStyles;
